/**
 * F3 (funnel-truth, FT-AC-088/089): the ONE place attribution model ids map
 * to human copy — the Paid Acquisition caption and selector both read this
 * dictionary, so a label can never drift from the id it names (D-F3-7).
 *
 * WHICH model is active always derives from the served payload
 * (`campaignEconomicsAttribution.selectedModelId`); this module only words
 * KNOWN ids. An unknown id renders verbatim — honest, never guessed.
 */

export interface AttributionModelCopy {
  /** Short human name that leads the visible caption («7-day click»). */
  label: string;
  /** One-clause description completing the caption sentence. */
  description: string;
}

const MODEL_COPY: Record<string, AttributionModelCopy> = {
  single_touch_user_scoped_7d_v1: {
    label: '7-day click',
    description: 'a paid click is credited to the person for 7 days',
  },
  single_touch_session_scoped_v1: {
    label: 'session click',
    description: 'credited only within the visit that clicked',
  },
};

/** Known id → copy; unknown id → the raw id as label, no description. */
export function attributionModelCopy(modelId: string): AttributionModelCopy {
  return MODEL_COPY[modelId] ?? { label: modelId, description: '' };
}

/**
 * D-F3-5 / FT-AC-044: the view-through divergence is PERMANENT — this clause
 * always accompanies the caption and the Meta-evidence column note.
 */
export const VIEW_THROUGH_DIVERGENCE_NOTE =
  'Meta also counts view-through conversions we cannot observe';

/**
 * People-attribution S3 (D-F, AC-023): the Meta column is read LIKE-FOR-LIKE —
 * the 7-day-click bucket is the comparable number (same window as the served
 * model, click-only); view-through is named as its own bucket, never folded
 * into a vague «can run higher».
 */
export const META_EVIDENCE_COLUMN_NOTE =
  'Meta purchases in the 7-day-click bucket — the same window as our model, click-only, so the two numbers compare like-for-like. '
  + `${VIEW_THROUGH_DIVERGENCE_NOTE}; those are shown as a separate view-through line, not added to the comparable figure.`;

/** D-F3-6: typed absence when the payload carries no attribution block. */
export const ATTRIBUTION_ABSENCE_LABEL =
  'Attribution model unavailable on this cached payload — press Refresh';
